import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import Header from '../Layout/Header';
import { useFirebase } from '../../hooks/useFirebase';
import { getParticipantUrl, logEnvironmentInfo } from '../../utils/urlUtils';
import './PresenterView.css';

const PresenterView: React.FC = () => {
  const { connectionStatus, participantCount, responses, currentQuestion } = useFirebase();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  const calculateAverage = () => {
    const values = Object.values(responses).map((r: any) => r.value);
    if (values.length === 0) return 0;
    return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
  };

  const responseCount = Object.keys(responses).length;
  const average = calculateAverage();

  // Get the participant URL using centralized utility
  const participantUrl = getParticipantUrl();

  // Generate QR code
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Log environment info for debugging
        logEnvironmentInfo();
        
        const url = await QRCode.toDataURL(participantUrl, {
          width: 180,
          margin: 2,
          color: {
            dark: '#435F60', // Dory Green
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M'
        });
        setQrCodeUrl(url);
        console.log('🔗 QR Code generated for URL:', participantUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [participantUrl]);

  // Render initial state (no responses yet)
  if (responseCount === 0) {
    return (
      <div className="presenter-view">
        <div className="presenter-initial-header">
          <h1 className="dory-title">Dory</h1>
        </div>
        
        <main className="presenter-initial-main">
          <div className="join-session-card">
            <div className="join-session-content">
              <h2>Join Session</h2>
              <div className="qr-code-wrapper-initial">
                {qrCodeUrl && (
                  <img 
                    src={qrCodeUrl} 
                    alt="QR Code to join session"
                    className="qr-code-image"
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render active state (responses received)
  return (
    <div className="presenter-view">
      <Header
        showLogo={true}
        participantCount={participantCount}
        connectionStatus={connectionStatus}
        title="Presenter Dashboard"
      />
      
      <main className="presenter-main">
        <div className="container">
          <div className="presenter-section">
            
            <div className="stats-bar">
              <div className="stat-item">
                <span className="stat-label">Participants:</span>
                <span className="stat-value">{participantCount}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Responses:</span>
                <span className="stat-value">{responseCount}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Average:</span>
                <span className="stat-value">{average}</span>
              </div>
            </div>

            <div className="results-display">
              <h3>{currentQuestion ? `"${currentQuestion}"` : 'Question Results'}</h3>
              
              <div className="results-visualization">
                <div className="response-dots">
                  {Object.entries(responses).map(([socketId, response]: [string, any]) => (
                    <div
                      key={socketId}
                      className="response-dot"
                      style={{
                        left: `${response.value}%`,
                        backgroundColor: '#9CB0BD' // Dory Lighter Blue
                      }}
                      title={`Response: ${response.value}`}
                    />
                  ))}
                  {/* Average indicator */}
                  <div
                    className="average-dot"
                    style={{
                      left: `${average}%`,
                      backgroundColor: '#435F60' // Dory Green
                    }}
                    title={`Average: ${average}`}
                  />
                </div>
                
                <div className="results-scale">
                  <span className="scale-label">0</span>
                  <span className="scale-label average-label">Average: {average}</span>
                  <span className="scale-label">100</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default PresenterView; 